import { useCallback, useEffect, useRef, useState } from "react";
import { twmRepository } from "../repositories/twm.repository";
import { shouldMakeAPICall } from "../utils/network";

export const useOfflineTWM = (
  authToken,
  userToken,
  page = 1,
  search = "",
  status = "all"
) => {
  const [twmList, setTwmList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(null);
  const requestIdRef = useRef(0);
  const [approversLoading, setApproversLoading] = useState(false);
  const [approversError, setApproversError] = useState(false);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;
    let basePageReceived = false;
    setLoading(true);
    setApproversLoading(false);
    setApproversError(false);
    const applyPage = ({ list, meta }) => {
      if (!isCurrent()) return;
      setTwmList(Array.isArray(list) ? list : []);
      setPaginationData(meta ? {
        ...meta,
        current_page_count: meta.total_count ?? 0,
        page_count: meta.total_pages ?? 0,
      } : null);
      setLoading(false);
    };

    const safePage = (() => {
      const n = Number(page);
      return Number.isFinite(n) && n > 0 ? n : 1;
    })();
    const safeSearch = String(search || "").trim();
    const safeStatus = String(status || "all").trim() || "all";

    if (shouldMakeAPICall(authToken, userToken)) {
      try {
        await twmRepository.syncPendingActions(authToken);
      } catch (err) {
        console.error("TWM syncPendingActions failed:", err?.message);
      }
      if (!isCurrent()) return;

      try {
        const { list, meta } = await twmRepository.fetchPageFromAPI(
          authToken,
          userToken,
          safePage,
          safeSearch,
          safeStatus,
          (basePage) => {
            if (!isCurrent()) return;
            basePageReceived = true;
            setApproversLoading(true);
            applyPage(basePage);
          }
        );

        applyPage({ list, meta });
        if (isCurrent()) setApproversLoading(false);
      } catch (err) {
        console.error("TWM fetchPageFromAPI failed:", err?.message);
        if (!isCurrent()) return;
        setApproversLoading(false);
        setApproversError(true);
        // Keep the fresh list if only the approval request failed.
        // Approval actions remain unavailable until a successful refresh.
        if (!basePageReceived) {
          const cached = await twmRepository.getFromCache();
          if (!isCurrent()) return;
          setTwmList(Array.isArray(cached) ? cached : []);
          setPaginationData(null);
        }
      }
    } else {
      const cached = await twmRepository.getFromCache();
      if (!isCurrent()) return;
      setTwmList(Array.isArray(cached) ? cached : []);
      setPaginationData(null);
    }

    if (isCurrent()) setLoading(false);
  }, [authToken, page, search, status, userToken]);

  useEffect(() => {
    load();
    return () => { requestIdRef.current += 1; };
  }, [load]);

  const refetch = useCallback(() => load(), [load]);

  return { twmList, loading, approversLoading, approversError, paginationData, refetch };
};
